import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
} from '@mui/material';
import {
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotify, useDataProvider } from 'react-admin';
import { StandaloneLayout } from '../StandaloneLayout';

interface Invitation {
  id: string;
  tenantCode: string;
  role: string;
  status: string;
  createdAt: string;
  tenant: {
    name: string;
    description?: string;
    logoUrl?: string;
  };
  invitedBy: {
    name?: string;
    email: string;
  };
}

interface Driver {
  id: number;
  profileState?: string;
  createdAt: string;
  updatedAt: string;
}

export const StandaloneDriverDashboard = () => {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);

  const navigate = useNavigate();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(userData);

      // Load invitations
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');

      const invitationsResponse = await fetch(`${baseUrl}/tenant-invitations/my-invitations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (invitationsResponse.ok) {
        const invitationsData = await invitationsResponse.json();
        setInvitations(invitationsData);
      }

      // Check if user has a driver profile
      try {
        const { data: driverData } = await dataProvider.getOne('drivers', { id: 'me' });
        if (driverData) {
          setDriver(driverData);
        }
      } catch (err) {
        // No driver profile yet (404 or null response)
        console.log('No driver profile found');
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      notify('Failed to load dashboard data', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to accept invitation');
      }

      const accepted = await response.json();
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const storedTenants = Array.isArray(storedUser.tenants) ? storedUser.tenants : [];
      const hasAcceptedTenant = storedTenants.some(
        (tenantMembership: any) => tenantMembership?.tenantCode === accepted.tenantCode
      );

      if (!hasAcceptedTenant) {
        storedTenants.push({
          tenantCode: accepted.tenantCode,
          roles: accepted.role ? [accepted.role] : [],
          tenant: accepted.tenant,
        });
      }

      localStorage.setItem(
        'user',
        JSON.stringify({
          ...storedUser,
          tenants: storedTenants,
        })
      );

      if (accepted?.tenant) {
        localStorage.setItem('currentTenant', JSON.stringify(accepted.tenant));
      }
      localStorage.removeItem('isStandalone');

      notify('Invitation accepted! Redirecting...', { type: 'success' });

      // Switch to the company dashboard after joining a tenant.
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error: any) {
      notify(error.message || 'Failed to accept invitation', { type: 'error' });
    }
  };

  const handleDeclineInvitation = async (invitationId: string) => {
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const token = localStorage.getItem('accessToken');

      const response = await fetch(`${baseUrl}/tenant-invitations/${invitationId}/decline`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to decline invitation');
      }

      notify('Invitation declined', { type: 'info' });
      loadData(); // Refresh data
    } catch (error: any) {
      notify(error.message || 'Failed to decline invitation', { type: 'error' });
    }
  };

  const handleCreateProfile = () => {
    navigate('/drivers/profile');
  };

  const handleEditProfile = () => {
    navigate('/drivers/profile');
  };

  const isProfileIncomplete = driver && (!driver.profileState || driver.profileState !== 'complete');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StandaloneLayout>
      <Box sx={{ maxWidth: 1200, margin: '0 auto' }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
          Welcome, {user?.name || user?.email}!
        </Typography>

      <Grid container spacing={3}>
        {/* Driver Profile Section */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BadgeIcon sx={{ mr: 1, fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  My Driver Profile
                </Typography>
              </Box>

              {!driver ? (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    You haven't created your driver profile yet. Create one to get started!
                  </Alert>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<BadgeIcon />}
                    onClick={handleCreateProfile}
                  >
                    Create Driver Profile
                  </Button>
                </Box>
              ) : (
                <Box>
                  {isProfileIncomplete && (
                    <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
                      Your driver profile is incomplete. Complete it to build a comprehensive profile!
                    </Alert>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Profile Status
                    </Typography>
                    <Chip
                      label={driver.profileState === 'complete' ? 'Complete' : 'Incomplete'}
                      color={driver.profileState === 'complete' ? 'success' : 'warning'}
                      sx={{ mt: 0.5 }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body1">
                      {new Date(driver.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Updated
                    </Typography>
                    <Typography variant="body1">
                      {new Date(driver.updatedAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/drivers/profile')}
                    >
                      View Profile
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={handleEditProfile}
                    >
                      {isProfileIncomplete ? 'Complete Profile' : 'Edit with Wizard'}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invitations Section */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Pending Invitations ({invitations.length})
              </Typography>

              {invitations.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No pending invitations
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {invitations.map((invitation) => (
                    <Paper
                      key={invitation.id}
                      elevation={2}
                      sx={{ mb: 2, p: 2, borderRadius: 2 }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {invitation.tenant.name}
                      </Typography>

                      {invitation.tenant.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {invitation.tenant.description}
                        </Typography>
                      )}

                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Invited by: {invitation.invitedBy.name || invitation.invitedBy.email}
                      </Typography>

                      <Chip
                        label={invitation.role}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={() => handleAcceptInvitation(invitation.id)}
                          fullWidth
                        >
                          Accept
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<CancelIcon />}
                          onClick={() => handleDeclineInvitation(invitation.id)}
                          fullWidth
                        >
                          Decline
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>
    </StandaloneLayout>
  );
};
